/**
 * Authentication State Store
 * Manages user authentication state and session information
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { setTokens, clearTokens as clearStoredTokens } from '@/utils/token';

/**
 * User type matching the API User model
 */
export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string | null;
  phoneNumber?: string | null;
  role: 'ADMIN' | 'MANAGER' | 'USER';
  platformRole?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING';
  mustChangePassword: boolean;
  lastLoginAt?: string | null;
  defaultWorkspaceId?: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Authentication tokens
 */
interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Auth State Interface
 */
interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // Tracks if auth state has been checked on app load
  currentWorkspaceId: number | null;

  // Actions
  setAuth: (user: User, tokens: AuthTokens, rememberMe?: boolean) => void;
  setUser: (user: User) => void;
  updateTokens: (tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setCurrentWorkspaceId: (workspaceId: number | null) => void;

  // Computed
  hasRole: (role: User['role'] | User['role'][]) => boolean;
  isAdmin: () => boolean;
  isManager: () => boolean;
  fullName: () => string;
}

/**
 * Initial state
 */
const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  currentWorkspaceId: null as number | null,
};

/**
 * Create the auth store with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Set auth state (login)
      setAuth: (user: User, tokens: AuthTokens, rememberMe?: boolean) => {
        // Store tokens using token utilities (handles rememberMe)
        setTokens(tokens.accessToken, tokens.refreshToken, rememberMe);

        const currentWsId = get().currentWorkspaceId;
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          // Auto-set workspace from user's default if not already set
          ...(currentWsId == null && user.defaultWorkspaceId
            ? { currentWorkspaceId: user.defaultWorkspaceId }
            : {}),
        });
      },

      // Update user data
      setUser: (user: User) => {
        const currentWsId = get().currentWorkspaceId;
        set({
          user,
          // Auto-set workspace from user's default if not already set
          ...(currentWsId == null && user.defaultWorkspaceId
            ? { currentWorkspaceId: user.defaultWorkspaceId }
            : {}),
        });
      },

      // Update tokens (e.g., after refresh)
      updateTokens: (tokens: AuthTokens) => {
        // Store tokens using token utilities (preserves rememberMe preference)
        setTokens(tokens.accessToken, tokens.refreshToken);

        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });
      },

      // Logout
      logout: () => {
        // Clear tokens from storage
        clearStoredTokens();

        // Reset state
        set(initialState);
      },

      // Set loading state
      setLoading: (loading: boolean) =>
        set({
          isLoading: loading,
        }),

      // Set initialized state (called after auth check on app load)
      setInitialized: (initialized: boolean) =>
        set({
          isInitialized: initialized,
          isLoading: false,
        }),

      // Set current workspace
      setCurrentWorkspaceId: (workspaceId: number | null) =>
        set({
          currentWorkspaceId: workspaceId,
        }),

      // Check if user has specific role(s)
      hasRole: (role: User['role'] | User['role'][]) => {
        const user = get().user;
        if (!user) return false;

        if (Array.isArray(role)) {
          return role.includes(user.role);
        }

        return user.role === role;
      },

      // Check if user is admin
      isAdmin: () => {
        const user = get().user;
        return user?.role === 'ADMIN';
      },

      // Check if user is manager or admin
      isManager: () => {
        const user = get().user;
        return user?.role === 'ADMIN' || user?.role === 'MANAGER';
      },

      // Get user's full name
      fullName: () => {
        const user = get().user;
        if (!user) return '';
        return `${user.firstName} ${user.lastName}`;
      },
    }),
    {
      name: 'skillspec-auth-store', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Don't persist tokens in store (they're in localStorage separately for API client)
      // Only persist user data and auth state
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentWorkspaceId: state.currentWorkspaceId,
        // accessToken and refreshToken are stored separately in localStorage
      }),
    }
  )
);

/**
 * Selector hooks for optimized re-renders
 */

// User selectors
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthInitialized = () => useAuthStore((state) => state.isInitialized);

// Auth actions
export const useAuthActions = () =>
  useAuthStore(
    useShallow((state) => ({
      setAuth: state.setAuth,
      setUser: state.setUser,
      updateTokens: state.updateTokens,
      logout: state.logout,
      setLoading: state.setLoading,
      setInitialized: state.setInitialized,
      setCurrentWorkspaceId: state.setCurrentWorkspaceId,
    }))
  );

// Workspace selector
export const useCurrentWorkspaceId = () => useAuthStore((state) => state.currentWorkspaceId);

// Role checks
export const useHasRole = (role: User['role'] | User['role'][]) =>
  useAuthStore((state) => state.hasRole(role));

export const useIsAdmin = () => useAuthStore((state) => state.isAdmin());
export const useIsManager = () => useAuthStore((state) => state.isManager());

// User info
export const useUserFullName = () => useAuthStore((state) => state.fullName());
